import * as React from 'react';

import { useBoolean, useLockFn } from 'ahooks';
import {
  Badge,
  Button,
  Divider,
  Form,
  Modal,
  notification,
  Select,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import { CheckOutlinedIcon, DeleteOutlinedIcon } from 'components';
import { useDiscountCodeValue } from 'context';
import { deleteDiscountCode } from 'services';
import { localizeDiscountCodeEnable, PATHNAME } from 'utils';

const {
  LOCAL: {
    DISCOUNT_CODES: { BASE_URL: DISCOUNT_CODES_BASE_URL },
  },
} = PATHNAME;

const discountCodeEnables = [true, false] satisfies boolean[];

type FieldType = Pick<ReturnType<typeof useDiscountCodeValue>, 'enable'>;

const DiscountCodeFormAction = () => {
  const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();
  const [deleteLoading, { toggle: toggleDeleteLoading }] = useBoolean();
  const { id, defaultEnable, mode } = useDiscountCodeValue();
  const navigate = useNavigate();

  const handleDeleteDiscountCode = useLockFn(
    async ({
      id,
    }: Pick<Required<ReturnType<typeof useDiscountCodeValue>>, 'id'>) => {
      toggleDeleteLoading();

      deleteDiscountCode({ id })
        .then(() => {
          navigate(DISCOUNT_CODES_BASE_URL);
          notification.success({
            message: 'کد تخفیف با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        })
        .finally(() => {
          toggleDeleteLoading();
        });
    },
  );

  const { label: defaultEnableLabel, color: defaultEnableColor } =
    localizeDiscountCodeEnable(defaultEnable);

  return (
    <div>
      <div className='px-6 pt-4'>
        <Typography.Title level={3} className='mb-0 text-base font-medium'>
          انتشار کد تخفیف
        </Typography.Title>
      </div>

      <Divider className='mb-8 mt-4' />

      <div className='px-6'>
        <Typography.Paragraph>
          <Badge
            text={`وضعیت فعلی: ${defaultEnableLabel}`}
            color={defaultEnableColor}
          />
        </Typography.Paragraph>
        <Form.Item<FieldType> label='وضعیت جدید' name='enable' className='mb-2'>
          <Select
            allowClear
            placeholder='وضعیت جدید کد تخفیف را انتخاب کنید'
            options={discountCodeEnables.map(enable => {
              const { label } = localizeDiscountCodeEnable(enable);
              return {
                label,
                value: enable,
              };
            })}
          />
        </Form.Item>
      </div>

      <div className='flex flex-wrap-reverse items-center gap-2 p-6 *:m-0 *:shrink *:grow *:basis-40'>
        {mode === 'Update' ? (
          <Form.Item>
            <Button
              block
              danger
              className='flex items-center'
              loading={deleteLoading}
              icon={<DeleteOutlinedIcon />}
              onClick={async () => {
                const confirmed = await deleteModalInstance.confirm({
                  title: 'آیا از حذف این کد تخفیف اطمینان دارید؟',
                  width: 500,
                });
                if (id && confirmed) {
                  handleDeleteDiscountCode({ id });
                }
              }}
            >
              <span>حذف کد تخفیف</span>
            </Button>
            {deleteModalContextHolder}
          </Form.Item>
        ) : null}

        <Form.Item>
          <Button
            block
            type='primary'
            htmlType='submit'
            icon={<CheckOutlinedIcon />}
          >
            {mode === 'Create' ? (
              <span>افزودن کد تخفیف جدید</span>
            ) : (
              <span>ذخیره تغییرات</span>
            )}
          </Button>
        </Form.Item>
      </div>
    </div>
  );
};

export { DiscountCodeFormAction };
